/**
 * Add or edit a quote.
 *
 * The coverage inputs are GENERATED FROM THE REQUIREMENTS for this line of coverage,
 * one field per checklist row, with the widget chosen by the requirement's operator.
 * Two things fall out of that, both deliberate:
 *
 *   * Every field you fill is a field that gets scored. There is no such thing as
 *     entering coverage detail that the compare table then ignores.
 *   * Editing the checklist (admin surface, later slice) reshapes this form. Nothing
 *     about the entry UI is hardcoded — same rule as categories and chore rates.
 *
 * "Not known yet" is a real, selectable answer that removes the key entirely, so the
 * compare table can show a dash. That is the difference between "they said no" and
 * "we never asked", and it must survive a round trip through this form.
 */
import { useMemo, useState } from 'react';
import { api, money, parseMoney } from '../../lib/api';
import type {
  InsuranceContact,
  InsuranceQuote,
  InsuranceQuoteStatus,
  InsuranceRequirement,
  QuoteCoverage,
} from '../../shared/types';
import { Button, Field, Select, TextInput, Textarea, errorText } from './ui';

const STATUS_OPTIONS: { value: InsuranceQuoteStatus; label: string; hint: string }[] = [
  { value: 'requested', label: 'Requested — waiting to hear back', hint: 'An ask is out, no numbers yet.' },
  { value: 'received', label: 'Received — quote in hand', hint: 'Enter the terms below.' },
  { value: 'declined', label: 'Declined — they will not write it', hint: 'Record why, so we do not re-ask.' },
  { value: 'expired', label: 'Expired — validity ran out', hint: 'Kept as history.' },
  { value: 'bound', label: 'Bound — this is the policy now', hint: '' },
];

/** '' is the single sentinel for "not known" across every widget. */
type Values = Record<string, string>;

function initCoverage(reqs: InsuranceRequirement[], coverage: QuoteCoverage): Values {
  const out: Values = {};
  for (const r of reqs) {
    const key = r.coverage_key;
    if (!key) continue;
    const v = coverage[key];
    if (v === undefined || v === null) out[key] = '';
    else if (typeof v === 'boolean') out[key] = v ? 'yes' : 'no';
    else if (typeof v === 'number') out[key] = (v / 100).toFixed(2);
    else out[key] = String(v);
  }
  return out;
}

export default function QuoteForm({
  policyType,
  requirements,
  contacts,
  quote,
  onSaved,
  onCancel,
}: {
  policyType: string;
  requirements: InsuranceRequirement[];
  contacts: InsuranceContact[];
  quote: InsuranceQuote | null;
  onSaved: (q: InsuranceQuote) => void;
  onCancel: () => void;
}) {
  /** Only requirements with a scoring rule get an input; the rest are read-the-document lines. */
  const scored = useMemo(
    () => requirements.filter((r) => r.coverage_key && r.operator),
    [requirements]
  );

  const [carrier, setCarrier] = useState(quote?.carrier ?? '');
  const [agency, setAgency] = useState(quote?.agency ?? '');
  const [quoteNumber, setQuoteNumber] = useState(quote?.quote_number ?? '');
  const [status, setStatus] = useState<InsuranceQuoteStatus>(quote?.status ?? 'requested');
  const [requestedDate, setRequestedDate] = useState(
    quote?.requested_date ?? new Date().toISOString().slice(0, 10)
  );
  const [contactId, setContactId] = useState(String(quote?.requested_contact_id ?? ''));
  const [receivedDate, setReceivedDate] = useState(quote?.received_date ?? '');
  const [validUntil, setValidUntil] = useState(quote?.valid_until ?? '');
  const [premium, setPremium] = useState(
    quote?.premium_cents != null ? (quote.premium_cents / 100).toFixed(2) : ''
  );
  const [fees, setFees] = useState(
    quote?.fees_cents != null ? (quote.fees_cents / 100).toFixed(2) : ''
  );
  const [outcome, setOutcome] = useState(quote?.outcome_reason ?? '');
  const [notes, setNotes] = useState(quote?.notes ?? '');
  const [values, setValues] = useState<Values>(() => initCoverage(scored, quote?.coverage ?? {}));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showTerms = status === 'received' || status === 'expired' || status === 'bound';
  const showOutcome = status === 'declined' || status === 'expired';

  /** Coverage keys already on the quote that no requirement covers — surfaced, never hidden. */
  const extraKeys = useMemo(() => {
    const known = new Set(scored.map((r) => r.coverage_key));
    return Object.keys(quote?.coverage ?? {}).filter((k) => !known.has(k));
  }, [quote, scored]);

  const previewTotal = useMemo(() => {
    const p = parseMoney(premium);
    if (p === null) return null;
    return p + (parseMoney(fees) ?? 0);
  }, [premium, fees]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (carrier.trim() === '') {
      setError('Carrier is required — it is how the quote is identified.');
      return;
    }
    if (premium !== '' && parseMoney(premium) === null) {
      setError('That premium is not a number we can read. Try 1200 or 1,200.00.');
      return;
    }
    if (fees !== '' && parseMoney(fees) === null) {
      setError('Those fees are not a number we can read.');
      return;
    }

    // Start from what is already stored so keys with no requirement (dwelling limit,
    // sublimits nobody has written a rule for) survive an edit instead of being wiped.
    const coverage: QuoteCoverage = { ...(quote?.coverage ?? {}) };
    for (const req of scored) {
      const key = req.coverage_key!;
      const raw = (values[key] ?? '').trim();

      if (raw === '') {
        delete coverage[key];
        continue;
      }
      if (req.operator === 'min' || req.operator === 'max') {
        const cents = parseMoney(raw);
        if (cents === null) {
          setError(`"${req.label}" needs a dollar amount, or leave it as not known.`);
          return;
        }
        coverage[key] = cents;
      } else if (req.operator === 'present' || req.operator === 'absent') {
        coverage[key] = raw === 'yes';
      } else {
        coverage[key] = raw;
      }
    }

    const body = {
      policy_type: policyType,
      carrier: carrier.trim(),
      agency: agency.trim() || null,
      quote_number: quoteNumber.trim() || null,
      requested_date: requestedDate || null,
      requested_contact_id: contactId === '' ? null : Number(contactId),
      received_date: showTerms ? receivedDate || null : null,
      valid_until: showTerms ? validUntil || null : null,
      premium_cents: showTerms ? parseMoney(premium) : null,
      fees_cents: showTerms ? parseMoney(fees) : null,
      total_cents: showTerms ? previewTotal : null,
      coverage,
      status,
      outcome_reason: showOutcome ? outcome.trim() || null : null,
      notes: notes.trim() || null,
    };

    setSaving(true);
    try {
      const saved = quote
        ? await api.put<InsuranceQuote>(`/insurance/quotes/${quote.id}`, body)
        : await api.post<InsuranceQuote>('/insurance/quotes', body);
      onSaved(saved);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {error && (
        <p role="alert" className="rounded-lg border border-clay/40 bg-clay-soft px-3 py-2 text-sm text-ink">
          {error}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Carrier" hint="Who would actually write the policy.">
          <TextInput
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            required
            autoFocus
            placeholder="Acme Mutual"
          />
        </Field>

        <Field label="Agency or broker" hint="Leave blank if you went direct.">
          <TextInput value={agency} onChange={(e) => setAgency(e.target.value)} />
        </Field>

        <Field label="Where it stands">
          <Select value={status} onChange={(e) => setStatus(e.target.value as InsuranceQuoteStatus)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Quote number" hint="Optional until they send paperwork.">
          <TextInput value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} />
        </Field>

        <Field label="Date asked" hint="When the request went out.">
          <TextInput
            type="date"
            value={requestedDate}
            onChange={(e) => setRequestedDate(e.target.value)}
          />
        </Field>

        <Field label="Who we asked">
          <Select value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">Nobody recorded</option>
            {contacts.map((con) => (
              <option key={con.id} value={String(con.id)}>
                {con.name}
                {con.org ? ` — ${con.org}` : ''}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {showTerms && (
        <fieldset className="space-y-4 rounded-card border border-border p-4">
          <legend className="px-1 text-sm font-medium text-ink">The numbers</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date received">
              <TextInput
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
            </Field>
            <Field label="Valid until" hint="What the renewal countdown reads.">
              <TextInput
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </Field>
            <Field label="Premium" hint="The base number, before fees.">
              <TextInput
                value={premium}
                onChange={(e) => setPremium(e.target.value)}
                inputMode="decimal"
                placeholder="1200.00"
              />
            </Field>
            <Field
              label="Fees and taxes"
              hint="Policy fee, inspection, surplus lines tax, stamping. Not a rounding error."
            >
              <TextInput
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                inputMode="decimal"
                placeholder="150.00"
              />
            </Field>
          </div>
          <p className="text-sm text-ink-soft">
            Total compared:{' '}
            <span className="font-semibold text-ink tabular-nums">
              {previewTotal === null ? '—' : money(previewTotal)}
            </span>
          </p>
        </fieldset>
      )}

      {showTerms && scored.length > 0 && (
        <fieldset className="space-y-4 rounded-card border border-border p-4">
          <legend className="px-1 text-sm font-medium text-ink">What it covers</legend>
          <p className="text-sm text-ink-soft">
            One field per requirement on your checklist. Anything left as{' '}
            <em>not known yet</em> shows as a dash on the compare table — a question to ask,
            not a pass.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {scored.map((req) => {
              const key = req.coverage_key!;
              const value = values[key] ?? '';
              const set = (v: string) => setValues((prev) => ({ ...prev, [key]: v }));

              if (req.operator === 'present' || req.operator === 'absent') {
                const yesLabel = req.operator === 'present' ? 'Yes — carried' : 'Yes — excluded';
                const noLabel = req.operator === 'present' ? 'No — not carried' : 'No — not excluded';
                return (
                  <Field key={req.id} label={req.label} hint={req.detail ?? undefined}>
                    <Select value={value} onChange={(e) => set(e.target.value)}>
                      <option value="">Not known yet</option>
                      <option value="yes">{yesLabel}</option>
                      <option value="no">{noLabel}</option>
                    </Select>
                  </Field>
                );
              }

              const isMoneyField = req.operator === 'min' || req.operator === 'max';
              return (
                <Field
                  key={req.id}
                  label={req.label}
                  hint={
                    isMoneyField
                      ? req.threshold_cents !== null
                        ? `Your rule: ${req.operator === 'min' ? 'at least' : 'at most'} ${money(req.threshold_cents)}`
                        : req.detail ?? undefined
                      : req.threshold_text
                        ? `Your rule: ${req.operator === 'equals' ? 'must be' : 'must not be'} ${req.threshold_text.toUpperCase()}`
                        : req.detail ?? undefined
                  }
                >
                  <TextInput
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    inputMode={isMoneyField ? 'decimal' : undefined}
                    placeholder={isMoneyField ? '50000.00' : 'Not known yet'}
                  />
                </Field>
              );
            })}
          </div>

          {extraKeys.length > 0 && (
            <p className="text-xs leading-relaxed text-ink-soft">
              Also on file for this quote, with no checklist rule to score it against:{' '}
              <span className="text-ink">{extraKeys.join(', ')}</span>. Editing here leaves those
              untouched.
            </p>
          )}
        </fieldset>
      )}

      {showOutcome && (
        <Field
          label={status === 'declined' ? 'Why they declined' : 'Why it lapsed unbound'}
          hint="The reason is the point — it stops the same call being made twice next renewal."
        >
          <Textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} rows={2} />
        </Field>
      )}

      <Field label="Notes" hint="Anything to remember when you pick this back up.">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving…' : quote ? 'Save changes' : 'Add to the board'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
